import api from './api';
import type { LoginCredentials, AuthResponse, User, Role } from '../types';

interface ApiEnvelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

interface ApiLoginPayload {
  accessToken: string;
  refreshToken: string;
  user: {
    user_id: string;
    tenant_id: string | null;
    email: string;
    first_name: string;
    last_name: string;
    role: Role;
    phone: string | null;
    employee_id: string | null;
  };
}

function mapUser(payload: ApiLoginPayload['user']): User {
  const name = [payload.first_name, payload.last_name].filter(Boolean).join(' ').trim();
  return {
    id: payload.user_id,
    email: payload.email,
    name: name || payload.email,
    role: payload.role,
    tenantId: payload.tenant_id ?? undefined,
    employeeId: payload.employee_id ?? undefined,
    isActive: true,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function toAuthResponse(payload: ApiLoginPayload): AuthResponse {
  return {
    user: mapUser(payload.user),
    tokens: {
      accessToken: payload.accessToken,
      refreshToken: payload.refreshToken,
    },
  };
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await api.post<ApiEnvelope<ApiLoginPayload>>('/api/auth/login', credentials);
    return toAuthResponse(response.data.data);
  },

  async logout(): Promise<void> {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        await api.post('/api/auth/logout', { refreshToken });
      }
    } catch {
      // Ignore errors on logout
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('field-force-auth');
    }
  },

  async refreshToken(): Promise<AuthResponse> {
    const refreshToken = localStorage.getItem('refresh_token');
    const response = await api.post<ApiEnvelope<{ accessToken: string }>>('/api/auth/refresh', {
      refreshToken,
    });
    const user = this.getPersistedUser();
    if (!user) {
      throw new Error('No persisted user');
    }
    return {
      user,
      tokens: {
        accessToken: response.data.data.accessToken,
        refreshToken: refreshToken ?? undefined,
      },
    };
  },

  async getMe(): Promise<User> {
    const response = await api.get<ApiEnvelope<ApiLoginPayload['user']>>('/api/auth/me');
    return mapUser(response.data.data);
  },

  persistAuth(response: AuthResponse): void {
    localStorage.setItem('access_token', response.tokens.accessToken);
    if (response.tokens.refreshToken) {
      localStorage.setItem('refresh_token', response.tokens.refreshToken);
    }
    localStorage.setItem('auth_user', JSON.stringify(response.user));
  },

  getPersistedUser(): User | null {
    const raw = localStorage.getItem('auth_user');
    if (!raw) return null;
    try {
      return JSON.parse(raw) as User;
    } catch {
      return null;
    }
  },

  getPersistedToken(): string | null {
    return localStorage.getItem('access_token');
  },
};
